import type { GraphType } from '@/graphql/types';
import type { AxisType } from '../../../../../graph/axis';
import type { ValueScaleEquationType } from '../../../../../graph/auto-scale';
import { getDataType } from '../../../../../graph/data-types';
import { DATA_TYPE, type DataTypeValue, type ScaleType } from '../../../../../graph/enums';
import {
    LEGEND_PLACEMENT,
    LEGEND_PLACEMENTS,
    resolveLegendPlacement,
    type LegendPlacement,
} from '../../../../../graph/legend-placement';
import type { AssertEqual } from '../../../../../utils/types';
import type { ElementDefinitionWithSettings, ElementSettings } from '../types';

export { LEGEND_PLACEMENT, LEGEND_PLACEMENTS, type LegendPlacement };

export const DEFAULT_GRAPH_SIZE = {
    width: 680,
    height: 285,
} as const;

export const SUPPORTED_GRAPH_TYPES = ['velocity', 'frequency', 'veff_day'] as const;
export type SupportedGraphType = (typeof SUPPORTED_GRAPH_TYPES)[number];

export const DATA_TYPE_VALUES = Object.values(DATA_TYPE) as readonly DataTypeValue[];

type DataTypeCapability = 'showFrequencyGraph' | 'showVeffDayGraph';

export type GraphBaseSettings = {
    graphType: SupportedGraphType;
    width: number;
    height: number;
};

export type GraphBehaviorSettings = {
    legendPlacement?: LegendPlacement;
    preferredGraphType?: GraphType;
    frequencyGraphScaleType?: ScaleType;
    dataType?: DataTypeValue;
    valueScale?: number | null;
    valueScaleEquation?: ValueScaleEquationType;
    frequencyScale?: number | null;
    hiddenLines?: string[];
    enabledAxes?: AxisType[];
};

type GraphBehaviorSettingsKey = keyof GraphBehaviorSettings;

const GRAPH_TYPE_CONTRACTS = {
    velocity: {
        dataTypeCapability: null,
        ownedDataType: null,
        hiddenLineExcludeTypes: [],
        settingNames: [
            'legendPlacement',
            'preferredGraphType',
            'dataType',
            'valueScale',
            'valueScaleEquation',
            'hiddenLines',
            'enabledAxes',
        ],
    },
    frequency: {
        dataTypeCapability: 'showFrequencyGraph',
        ownedDataType: null,
        hiddenLineExcludeTypes: [],
        settingNames: [
            'legendPlacement',
            'frequencyGraphScaleType',
            'dataType',
            'valueScale',
            'valueScaleEquation',
            'frequencyScale',
            'hiddenLines',
            'enabledAxes',
        ],
    },
    veff_day: {
        dataTypeCapability: 'showVeffDayGraph',
        ownedDataType: DATA_TYPE.VEFF,
        // Keep aligned with VeffDayGraph.getLegendExcludeTypes$(): Veff/day has no event
        // provider and excludes the Event legend type (`m`).
        hiddenLineExcludeTypes: ['m'],
        settingNames: [
            'legendPlacement',
            'dataType',
            'valueScale',
            'valueScaleEquation',
            'hiddenLines',
        ],
    },
} as const satisfies Record<
    SupportedGraphType,
    {
        dataTypeCapability: DataTypeCapability | null;
        ownedDataType: DataTypeValue | null;
        hiddenLineExcludeTypes: readonly string[];
        settingNames: readonly GraphBehaviorSettingsKey[];
    }
>;

export type GraphSettingsShape = GraphBaseSettings & GraphBehaviorSettings;
export type GraphSettings = GraphSettingsShape & ElementSettings;
export type GraphElementDefinition = ElementDefinitionWithSettings<'graph', GraphSettingsShape>;

export function isSupportedGraphType(value: unknown): value is SupportedGraphType {
    return typeof value === 'string' && SUPPORTED_GRAPH_TYPES.includes(value as SupportedGraphType);
}

export function getGraphTypeOwnedDataTypeSettings(
    graphType: SupportedGraphType
): Pick<GraphBehaviorSettings, 'dataType'> {
    const { ownedDataType } = GRAPH_TYPE_CONTRACTS[graphType];
    return ownedDataType === null ? {} : { dataType: ownedDataType };
}

export function getGraphTypeFormSettingNames(
    graphType: SupportedGraphType
): readonly GraphBehaviorSettingsKey[] {
    const { ownedDataType, settingNames } = GRAPH_TYPE_CONTRACTS[graphType];
    return ownedDataType === null
        ? settingNames
        : settingNames.filter((settingName) => settingName !== 'dataType');
}

export function getGraphTypePersistedSettingNames(
    graphType: SupportedGraphType
): readonly GraphBehaviorSettingsKey[] {
    return GRAPH_TYPE_CONTRACTS[graphType].settingNames;
}

export function getGraphTypeHiddenLineExcludeTypes(
    graphType: SupportedGraphType
): readonly string[] {
    return GRAPH_TYPE_CONTRACTS[graphType].hiddenLineExcludeTypes;
}

export const GRAPH_SETTINGS_ALLOWED_KEYS = [
    'graphType',
    'width',
    'height',
    'legendPlacement',
    'preferredGraphType',
    'frequencyGraphScaleType',
    'dataType',
    'valueScale',
    'valueScaleEquation',
    'frequencyScale',
    'hiddenLines',
    'enabledAxes',
] as const satisfies readonly (keyof GraphSettingsShape)[];
export type GraphSettingsKey = (typeof GRAPH_SETTINGS_ALLOWED_KEYS)[number];

const graphSettingsAllowlistCoversShape: AssertEqual<GraphSettingsKey, keyof GraphSettingsShape> =
    true;
void graphSettingsAllowlistCoversShape;

export function resolveReportBuilderLegendPlacement(
    currentSettings?: ElementSettings
): LegendPlacement {
    return currentSettings
        ? resolveLegendPlacement(currentSettings.legendPlacement)
        : LEGEND_PLACEMENT.HEADER;
}

export function createDefaultGraphSettings(): GraphBaseSettings &
    Required<Pick<GraphBehaviorSettings, 'legendPlacement'>> {
    return {
        graphType: 'velocity',
        width: DEFAULT_GRAPH_SIZE.width,
        height: DEFAULT_GRAPH_SIZE.height,
        legendPlacement: resolveReportBuilderLegendPlacement(),
    };
}

export function createGraphDefinition(
    settings: GraphSettingsShape = createDefaultGraphSettings()
): GraphElementDefinition {
    return {
        type: 'graph',
        settings,
    };
}

export function dataTypeSupportsGraphType(
    dataType: DataTypeValue,
    graphType: SupportedGraphType
): boolean {
    const capability = GRAPH_TYPE_CONTRACTS[graphType].dataTypeCapability;
    return capability === null || getDataType(dataType)[capability];
}

export function getAvailableGraphTypes(
    dataTypes: readonly DataTypeValue[]
): readonly SupportedGraphType[] {
    return SUPPORTED_GRAPH_TYPES.filter((graphType) =>
        dataTypes.some((dataType) => dataTypeSupportsGraphType(dataType, graphType))
    );
}
